'use client'

import { useState, useEffect } from 'react'
import { useRouter, useSearchParams } from '@/lib/react-compat/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Sprout, AlertCircle, Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react'
import { useAuth } from '@/components/auth-context'
import { AuthPageSkeleton } from '@/components/loading-skeletons'
import Link from '@/lib/react-compat/link'
import { useI18n } from '@/components/i18n-provider'

function LoginPageInner() {
  const { t } = useI18n()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { login, isAuthenticated } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()

  // 如果已经登录，重定向到首页
  useEffect(() => {
    if (isAuthenticated) {
      const returnUrl = searchParams.get('returnUrl') || '/'
      router.replace(returnUrl)
    }
  }, [isAuthenticated, router, searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const success = await login(username, password)
      if (success) {
        const returnUrl = searchParams.get('returnUrl') || '/'
        router.replace(returnUrl)
      } else {
        setError(t('login.errorInvalid'))
      }
    } catch {
      setError(t('login.errorFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  // 如果已经登录，显示加载状态
  if (isAuthenticated) {
    return <AuthPageSkeleton />
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-muted p-6 md:p-10">
      <div className="absolute left-4 top-4">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => router.replace('/')}
          className="text-muted-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          返回
        </Button>
      </div>

      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2 text-center">
          <div className="flex size-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Sprout className="size-5" />
          </div>
          <h1 className="text-xl font-semibold">{t('brand')}</h1>
          <p className="text-sm text-muted-foreground">{t('login.product')}</p>
        </div>

        <Card>
          <CardHeader className="text-center">
            <CardTitle className="text-lg">{t('login.submit')}</CardTitle>
            <CardDescription>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">{t('login.usernameLabel')}</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder={t('login.usernamePlaceholder')}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  disabled={isLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('login.passwordLabel')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.passwordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                {isLoading ? t('login.submitting') : t('login.submit')}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                {t('login.noAccount')}{' '}
                <Link href="/register" className="font-medium text-foreground underline-offset-4 hover:underline">
                  {t('login.registerLink')}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">{t('login.slogan')}</p>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return <LoginPageInner />
}
